import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import TitleDescription from './../TitleDescription/TitleDescription';
import CircleTitle from './../CircleTitle/CircleTitle';
import { AdminInput, AdminSelect, AdminCheckbox, AdminModal } from '@project/components';
import { RootContext } from '../../hoc/RootContext/RootContext';
import axios from '../../utils/axios';
import NumberFormat from 'react-number-format';
import { USER_TITLES } from '../../constants/index';
import { useIntl } from 'react-intl';
import Title from '../Title/Title';
import PageContentContainer from '../PageContentContainer/PageContentContainer';
import TwoColumnContent from './../../components/TwoColumnContent/TwoColumnContent';
import LeftColumn from './../../components/TwoColumnContent/LeftColumn/LeftColumn';
import RightColumn from './../../components/TwoColumnContent/RightColumn/RightColumn';
import styles from './Settings.module.css';
import permissions from './../../containers/Dashboard/permissions'
import { IoCloudUploadOutline, IoCheckmark } from 'react-icons/io5';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import { get } from '../../utils/utils'
import { States } from '@project/components';
import COITable from './COITable';
import moment from 'moment';

const Settings = () => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({id});
  
  const { currentUser, getUser, setShowToast } = React.useContext(RootContext);

  const uploadRef = useRef(null);
  const uploadCOIRef = useRef(null);
  const [errors, setErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCOI, setIsSavingCOI] = useState(false);
  const [formState, setFormState] = useState({
    fname: currentUser.fname,
    lname: currentUser.lname,
    phone: currentUser.phone,
    title: currentUser.title
  });
  const [vendorCompanyProfileState, setVendorCompanyProfileState] = useState({
    company: currentUser?.vendorCompanyInformation?.company ?? '',
    contactemail: currentUser?.vendorCompanyInformation?.contactemail ?? '',
    address1: currentUser?.vendorCompanyInformation?.address1 ?? '',
    city: currentUser?.vendorCompanyInformation?.city ?? '',
    postal: currentUser?.vendorCompanyInformation?.postal ?? '',
    state: currentUser?.vendorCompanyInformation?.state ?? ''
  });
  const [disableQc, setDisableQc] = useState(currentUser.qcDisabled ?? false)
  const [showUploadCOIModal, setShowUploadCOIModal] = useState(false);
  const [uploadData, setUploadData] = useState(null);
  const [certificatesList, setCertificatesList] = useState([]);
  const [coiFile, setCoiFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [hasFileUploadErrors, setHasFileUploadErrors] = useState(false);

  // some simple validation for now
  const isValid = formState.fname && formState.lname;

  const stateOptions = States.map((state, index) => {
    return {
      id: index.toString(),
      label: state.label,
      value: state.value,
      disable: false
    };
  });

  const getCoiStatus = (certificate) => {
    const currentDate = moment();
    const insuranceStartDate = moment(certificate.insuranceStartDate);
    const insuranceEndDate = moment(certificate.insuranceEndDate);

    if (insuranceStartDate.isAfter(currentDate)) return t("term.notActive");
    if (insuranceStartDate.isSameOrBefore(currentDate) && insuranceEndDate.isAfter(currentDate)) return t("term.active");

    return t("term.expired");
  };


  const getCertificateOfInsurances = async () => {
    try {
      let result = await axios.get(`/vendors/${currentUser.username}/coi-files`);
      let coiFiles = get(result, 'data.records', []);
      coiFiles.forEach(coiFile => {
        coiFile.insuranceStartDate = coiFile.insuranceStartDate ? moment(coiFile.insuranceStartDate).format('MM/DD/YYYY') : '';
        coiFile.insuranceEndDate = coiFile.insuranceEndDate ? moment(coiFile.insuranceEndDate).format('MM/DD/YYYY') : '';
        coiFile.sentStatus = getCoiStatus(coiFile);
        coiFile.document = coiFile.UploadedFile?.dataValues?.filename;
      });
      setCertificatesList(result.data.records);
      setLoading(false);
      return;
    } catch (e) {
      setErrors((e && e.message) || t('error.server'));
    }
  }

  useEffect(() => {
    setLoading(true);
    getCertificateOfInsurances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasFileUploadErrors) {
      setTimeout(() => setHasFileUploadErrors(false), 2000);
    }
  }, [hasFileUploadErrors])


  const saveUserInfo = async (message, photoUpload) => {
    if (!isValid) return;
    if (formState.phone)
      formState.phone = formState.phone.replace(/[^0-9]*/g, '');

    const data = new FormData();
    if (photoUpload) data.append('profilepicture', photoUpload);
    Object.keys(formState).forEach(key =>
      data.append(key, formState[key])
    );
    data.append('qcDisabled', disableQc);
    data.append('type', currentUser.type);

    if (isUserAVendor()) {
      Object.keys(vendorCompanyProfileState).forEach(key =>
        data.append(`vendorCompanyInformation.${key}`, vendorCompanyProfileState[key])
      );
    }

    setIsSaving(true);
    try {
      let result = await axios.put(
        `/users/${currentUser.username}`,
        data
      );
      if (result.status === 200) {
        getUser();
        setShowToast({
          show: true,
          message: message ? message : t('Settings.saveSuccessful')
        });
      }
    } catch (e) {
      if (e.response && e.response.data) {
        setErrors(
          `${t('Settings.error.updates')} (${
            e.response.data.errors
          })`
        );
      } else {
        setErrors((e && e.message) || t('error.server'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onFormChange = (key, value) => {
    setFormState({ ...formState, [key]: value });
  }

  const onUploadDataChange = (key, value) => {
    setUploadData({ ...uploadData, [key]: value });
  };

  const onVendorCompanyProfileChange = (key, value) => {
    setVendorCompanyProfileState({ ...vendorCompanyProfileState, [key]: value });
  }

  const onProfileImageUpload = e => {
    saveUserInfo(t('Settings.profileImageUploaded'), e.target.files[0]);
  };

  const onProfileImageDelete = async () => {
    setIsSaving(true);
    try {
      let result = await axios.delete(`/users/${currentUser.username}/profile_picture`);
      if (result.status === 200) {
        getUser();
        setShowToast({ show: true, message: t('Settings.profileImageDeleted') });
      }
    } catch (e) {
      if (e.response && e.response.data) {
        setErrors(`${t('Settings.profileImageDeleted.error')} (${e.response.data.errors})`);
      } else {
        setErrors((e && e.message) || t('error.server'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isUserAVendor = () => {
    return permissions.myProperties.includes(get(currentUser, 'type'))
  }

  const initials = currentUser.fname && currentUser.lname ? currentUser.fname.substring(0, 1) + currentUser.lname.substring(0, 1) : currentUser.username.substring(0, 1);

  const uploadCOIFile = async (e) => {
    const data = new FormData();
    data.append('files', e.target.files[0]);
    data.append('type', 'Document');
    data.append('category', 'coi');

    try {
      setIsUploadingFile(true)
      let result = await axios.post(`/vendors/${currentUser.username}/files`, data, {
        validateStatus: (status) => {
          return status >= 200 && status < 300;
        }
      });
      if (result.status === 200) {
        setCoiFile(result.data.records[0]);
      }
    } catch (e) {
      if (e.response && e.response.data) {
        setErrors(`${t('Settings.certificateInsurance.uploadError')} (${e.response.data.errors})`);
      } else {
        setErrors((e && e.message) || t('error.server'));
      }
      setHasFileUploadErrors(true);
    } finally {
      setIsUploadingFile(false);

      // Reset the file input so the same file can be uploaded again
      if (uploadCOIRef.current) {
        uploadCOIRef.current.value = null;
      }
    }
  }

  const handleCloseCOIModal = () => {
    setShowUploadCOIModal(false);
    setCoiFile(null);
    setUploadData(null);
    setHasFileUploadErrors(false);
  }

  const saveCOIFile = async () => {
    const data = new FormData();
    data.append('file', coiFile);
    data.append('insuranceStartDate', uploadData.startDate);
    data.append('insuranceEndDate', uploadData.endDate);

    setIsSavingCOI(true);
    try {
      let result = await axios.put(
        `/vendors/${currentUser.username}/files/${coiFile.id}/insurance-dates`,
        data
      );
      if (result.status === 200) {
        setShowToast({ show: true, message: t('Settings.certificateInsurance.uploadSuccessful') });
      }
    } catch (e) {
      if (e.response && e.response.data) {
        setErrors(`${t('Settings.certificateInsurance.uploadError')} (${e.response.data.errors})`);
      } else {
        setErrors((e && e.message) || t('error.server'));
      }
    } finally {
      setIsSavingCOI(false);
      setLoading(true);
      getCertificateOfInsurances();
      handleCloseCOIModal();
    }
  }

  const renderFooter = () => (
    <>
      <Button variant="secondary" onClick={handleCloseCOIModal} className="btn-gray">
        {t('ui.cancel')}
      </Button>
      <Button variant="primary" disabled={!uploadData?.startDate || !uploadData?.endDate || !coiFile || isSavingCOI} onClick={saveCOIFile} className="btn-prime">
        {t('Settings.certificateInsurance.confirmUpload')}
      </Button>
    </>
  );

  const renderButtonContent = () => {
    if (hasFileUploadErrors) {
      return (
        <>
          <FaTimes className={styles.uploadFailIcon} />
          <span className={styles.uploadFailed}>{t('Settings.certificateInsurance.uploadFailureState')}</span>
        </>
      )
    }

    if (isUploadingFile) {
      return (
        <>
          <FaSpinner className={styles.uploadLoadingIcon} />
          <span>{t('Settings.certificateInsurance.uploadingState')}</span>
        </>
      );
    }
  
    if (coiFile) {
      return (
        <>
          <IoCheckmark className={styles.uploadIcon} />
          <span className={styles.uploadSuccessful}>{t('Settings.certificateInsurance.uploadSuccessState')}</span>
        </>
      );
    }
  
    return (
      <>
        <IoCloudUploadOutline className={styles.uploadIcon} />
        <span>{t('Settings.certificateInsurance.uploadState')}</span>
      </>
    );
  };

  return (
    <>
      <PageContentContainer>
        <Title
          title={t('Settings.header')}
          button={{ click: () => saveUserInfo(), label: t('ui.save'), disabled: isSaving || !isValid }}
        />

        {errors ? (
          <Alert key={errors} variant="danger" className="mt-3">
            {Array.isArray(errors) ? (
              errors.map((error, i) => <div key={`error${i}`}>{error.message}</div>)
            ) : (
              <>
                {t('error')}: {errors}
              </>
            )}
          </Alert>
        ) : null}
        <TwoColumnContent>
          <LeftColumn>
            <TitleDescription title={t('Settings.mainTitle')} description={t('Settings.description')} />
          </LeftColumn>
          <RightColumn padded={false} border={false}>
            <div className={`shadow-sm bg-white border ${styles.profile}`}>
              <div className="d-flex align-items-center py-3 px-1 ">
                <div className={`${styles.profilePicture} textLink`}>
                  {currentUser.profilePicture ? (
                    <img
                      alt="profile"
                      src={`${currentUser.profilePicture}?${Math.random()}`}
                      className="d-inline-block rounded-circle mr-3 bglink font14 circleTitle p-0"
                    />
                  ) : (
                    <CircleTitle title={initials.toUpperCase()} />
                  )}
                </div>
                <Button
                  variant="light"
                  className={'rounded-0 border mr-3 '}
                  onClick={() => uploadRef.current.click()}
                  disabled={isSaving}
                >
                  {t('Settings.uploadPhoto')}
                </Button>
                <input
                  ref={uploadRef}
                  type="file"
                  name="file"
                  onChange={onProfileImageUpload}
                  className="d-none"
                  accept=".jpg, .jpeg, .gif, .png"
                />
                <Button
                  variant="light"
                  className={'rounded-0 border'}
                  onClick={onProfileImageDelete}
                  disabled={isSaving || !currentUser.profilePicture}
                >
                  {t('Settings.removePhoto')}
                </Button>
              </div>
              <div className="border-bottom w-100"></div>
              <Form>
                <div className="d-flex pt-3">
                  <div className="col-sm-6">
                    <AdminInput
                      id="fname"
                      label={t('Settings.firstName')}
                      type="text"
                      value={formState.fname}
                      isInvalid={!formState.fname}
                      onChange={e => onFormChange('fname', e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6">
                    <AdminInput
                      id="lname"
                      label={t('Settings.lastName')}
                      required={true}
                      type="text"
                      value={formState.lname}
                      isInvalid={!formState.lname}
                      onChange={e => onFormChange('lname', e.target.value)}
                    />
                  </div>
                </div>
                <div className={`${isUserAVendor() ? 'd-none' : 'd-flex pt-3'}`}>
                  <div className="col-sm-12">
                    <AdminSelect
                      id="title"
                      label={t('Settings.title')}
                      type="text"
                      value={USER_TITLES.find(_ => _.value === formState.title)}
                      options={USER_TITLES}
                      onChange={e => onFormChange('title', e.value)}
                    />
                  </div>
                </div>
                <div className="d-flex pt-3 pb-4">
                  <div className="col-sm-6">
                    <AdminInput id="email" label={t('Settings.email')} controlCLass="d-none">
                      <div className="d-flex justify-content-between pt-2">
                        <span className="textLink">{currentUser.email}</span>
                        {/* <span className="textLink">Change</span> */}
                      </div>
                    </AdminInput>
                  </div>
                  <div className="col-sm-6">
                    <AdminInput id="phone" label={t('Settings.phone')}>
                      <NumberFormat
                        value={formState.phone}
                        format="(###) ###-####"
                        mask="_"
                        onChange={e => onFormChange('phone', e.target.value)}
                        className="form-control"
                      />
                    </AdminInput>
                  </div>
                </div>
              </Form>
            </div>
          </RightColumn>
        </TwoColumnContent>

        {/* Vendor Company Profile */}
        {isUserAVendor() ? (
          <>
            <TwoColumnContent>
              <LeftColumn>
                <TitleDescription title={t('Settings.vendorCompanyProfileTitle')} />
              </LeftColumn>
              <RightColumn>
                <Form>
                  <div className="d-flex pt-3 flex-wrap">
                    <div className="col-12 col-sm-6">
                      <AdminInput
                        id="companyName"
                        label={t('Settings.vendorCompanyProfile.companyName')}
                        type="text"
                        value={vendorCompanyProfileState.company}
                        onChange={e => onVendorCompanyProfileChange('company', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <AdminInput
                        id="companyWebsite"
                        label={t('Settings.vendorCompanyProfile.companyWebsite')}
                        required={true}
                        type="text"
                        value={vendorCompanyProfileState.contactemail}
                        onChange={e => onVendorCompanyProfileChange('contactemail', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="d-flex pt-3 flex-wrap">
                    <div className="col-12 col-sm-6">
                      <AdminInput
                        id="companyAddress"
                        label={t('Settings.vendorCompanyProfile.companyAddress')}
                        required={true}
                        type="text"
                        value={vendorCompanyProfileState.address1}
                        onChange={e => onVendorCompanyProfileChange('address1', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <AdminInput
                        id="city"
                        label={t('Settings.vendorCompanyProfile.city')}
                        required={true}
                        type="text"
                        value={vendorCompanyProfileState.city}
                        onChange={e => onVendorCompanyProfileChange('city', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="d-flex pt-3 flex-wrap">
                    <div className="col-12 col-sm-6">
                      <AdminInput
                        id="zipcode"
                        label={t('Settings.vendorCompanyProfile.zipcode')}
                        required={true}
                        type="number"
                        value={vendorCompanyProfileState.postal}
                        onChange={e => onVendorCompanyProfileChange('postal', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <AdminSelect
                        id="state"
                        label={t('Settings.vendorCompanyProfile.state')}
                        type="text"
                        value={stateOptions.find(_ => _.value === vendorCompanyProfileState.state)}
                        options={stateOptions}
                        onChange={e => onVendorCompanyProfileChange('state', e.value)}
                      />
                    </div>
                  </div>
                </Form>
              </RightColumn>
            </TwoColumnContent>

            <TwoColumnContent>
              <LeftColumn>
                <TitleDescription title={t('Settings.certificateInsuranceTitle')} />
              </LeftColumn>
              <RightColumn>
                <>
                  <div className="d-flex  justify-content-end align-items-center pb-3">
                    <Button size="sm" onClick={() => setShowUploadCOIModal(true)}>
                      {t('Settings.uploadCertificate')}
                    </Button>
                  </div>
                  <COITable certificates={certificatesList} loading={loading} />
                </>
              </RightColumn>
            </TwoColumnContent>
          </>
        ) : null}

        {currentUser.company === 'ID Plans' ? (
          <TwoColumnContent>
            <LeftColumn>
              <TitleDescription title={t('Settings.productTitle')} />
            </LeftColumn>
            <RightColumn>
              <h5>{t('Settings.disableQc.title')}</h5>
              <AdminCheckbox
                id={'disableQc'}
                label={t('Settings.disableQc.description')}
                onChange={e => setDisableQc(e.currentTarget.checked)}
                checked={disableQc}
                customstyle={styles.checkbox}
                removeMargins={true}
              />
            </RightColumn>
          </TwoColumnContent>
        ) : null}
      </PageContentContainer>

      <AdminModal
        show={showUploadCOIModal}
        onHide={handleCloseCOIModal}
        title={t('Settings.certificateInsurance.uploadTitle').toUpperCase()}
        noSave={true}
        onSave={() => saveCOIFile()}
        customFooterStyle={'justify-content-between'}
        isSaving={isSavingCOI}
        footer={renderFooter()}
      >
        <div className="d-flex justify-content-center align-items-center">
          <div className="w-2/3 d-flex h-full p-3">
            <button 
              className={hasFileUploadErrors ? styles.uploadContainerError : styles.uploadContainer} 
              onClick={() => uploadCOIRef.current.click()} 
              disabled={certificatesList.length === 40}
            >
              {renderButtonContent()}
            </button>
            <input
              type="file"
              ref={uploadCOIRef}
              className="d-none"
              onChange={e => {
                uploadCOIFile(e);
              }}
            />
          </div>
          <div className="w-1/3 d-flex pt-3 flex-wrap">
            <div className="col-12">
              <AdminInput
                id="startDate"
                label={t('Settings.certificateInsurance.modal.startDate')}
                required={true}
                type="date"
                onChange={e => onUploadDataChange('startDate', e.target.value)}
                value={uploadData?.startDate}
                isInvalid={!uploadData?.startDate}
              />
            </div>
            <div className="col-12">
              <AdminInput
                id="endDate"
                label={t('Settings.certificateInsurance.modal.endDate')}
                required={true}
                type="date"
                onChange={e => onUploadDataChange('endDate', e.target.value)}
                value={uploadData?.endDate}
                isInvalid={!uploadData?.endDate}
              />
            </div>
          </div>
        </div>
      </AdminModal>
    </>
  );
};

export default Settings;
